import React, { FC, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FormNavigationContext } from '@/shared/contexts/form-navigation.context';
import Head from '@/shared/components/seo/head';
import { useDocument } from '@/shared/hooks/useDocument';
import { Box, Paper, LoadingOverlay, Alert, Group, Button, Text, Modal, Drawer, ActionIcon, Menu, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFile, IconLayoutSidebarRight, IconMaximize, IconSquare, IconCheck, IconLayout2 } from '@tabler/icons-react';
import DocumentMenu from '@/features/forms/pages/document-menu';
import type { FormDisplayMode } from '@/core/types/documents';
import { RelativeTime } from '@/shared/components/ui/dateTime/relative-time';
import { queryClient } from '@/core/query/query.client';

interface FormLayoutProps {
    children: React.ReactNode;
    /** Display mode: 'navigation' (default) renders as full page, 'modal' renders in a modal */
    displayMode?: FormDisplayMode;
    /** Modal opened state (for controlled modal) */
    opened?: boolean;
    /** Callback when modal is closed */
    onClose?: () => void;
    /** Modal title override */
    modalTitle?: string;
    /** Modal size (for 'modal' mode) */
    modalSize?: string;
    /** Hide modal wrapper (used when modal is already wrapped by Modal.Stack) */
    hideModalWrapper?: boolean;
}

export const FormLayout: FC<FormLayoutProps> = ({
    children,
    displayMode: propDisplayMode,
    opened: controlledOpened,
    onClose: controlledOnClose,
    modalTitle: propModalTitle,
    modalSize = 'xl',
    hideModalWrapper = false
}) => {
    const urlParams = useParams<{ docType: string; action: string; docNo: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    
    const searchParams = new URLSearchParams(location.search);
    const isModalFromQuery = searchParams.get('formModal') === 'true';
    const modalDocType = searchParams.get('formDocType');
    const modalAction = searchParams.get('formAction');
    const modalDocNo = searchParams.get('formDocNo');

    // Only consume modal query params when this FormLayout is controlled by GlobalFormModal
    // (i.e. controlledOnClose is provided). When standalone (FormRoute), the GlobalFormModal
    // handles the overlay; we must use URL path params so FormLayout doesn't open a second modal.
    const useModalQuery = isModalFromQuery && !!controlledOnClose;

    const docType = useModalQuery ? (modalDocType ?? '') : (urlParams.docType ?? '');
    const action = useModalQuery ? (modalAction ?? '') : (urlParams.action ?? '');
    const docNo = useModalQuery ? (modalDocNo ?? undefined) : (urlParams.docNo ?? undefined);

    const { documentConfig } = useDocument(docType ?? '');

    const isProgrammaticOpen = ((location.state as { programmaticOpen?: boolean; displayMode?: FormDisplayMode })?.programmaticOpen ?? false) || useModalQuery;
    const stateDisplayMode = (location.state as { programmaticOpen?: boolean; displayMode?: FormDisplayMode })?.displayMode ??
                            (useModalQuery ? (searchParams.get('formDisplayMode') as FormDisplayMode) : undefined);

    // Determine display mode:
    // 1. If opened directly from URL (not programmatic), use documentConfig or default to navigation
    // 2. If opened programmatically, use: prop > state > documentConfig > default
    const displayMode = isProgrammaticOpen
        ? (propDisplayMode ?? stateDisplayMode ?? documentConfig?.formDisplayMode ?? 'modal')
        : (documentConfig?.formDisplayMode ?? 'navigation'); // Use document config or default to navigation

    const isModalMode = displayMode === 'modal' || displayMode === 'fullscreen' || displayMode === 'sidebar';

    const normalizedDocType = docType?.trim() ?? '';

    // Modal state management
    const [internalOpened, { open, close }] = useDisclosure(false);
    const modalOpened = controlledOpened ?? internalOpened;
    const handleModalClose = useCallback(() => {
        if (controlledOnClose) {
            controlledOnClose();
        } else {
            close();

            // Check if modal was opened via query parameters
            const searchParams = new URLSearchParams(location.search);
            const isModalFromQuery = searchParams.get('formModal') === 'true';
            
            if (isModalFromQuery) {
                // Remove form query parameters to close modal
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.delete('formModal');
                newSearchParams.delete('formDocType');
                newSearchParams.delete('formAction');
                newSearchParams.delete('formDocNo');
                newSearchParams.delete('formDisplayMode');
                navigate(`${location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`, { replace: true });
                
                // Invalidate master list queries to trigger data refresh
                if (normalizedDocType) {
                    queryClient.invalidateQueries({
                        queryKey: ['master', normalizedDocType],
                        exact: false
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['masterListRowCount', normalizedDocType],
                        exact: false
                    });
                }
            } else if (isModalMode && normalizedDocType) {
                // In modal mode (from URL route), refresh data and navigate to master page
                queryClient.invalidateQueries({
                    queryKey: ['master', normalizedDocType],
                    exact: false
                });
                queryClient.invalidateQueries({
                    queryKey: ['masterListRowCount', normalizedDocType],
                    exact: false
                });
                navigate(`/master/${normalizedDocType}`, { replace: true });
            } else {
                // In navigation mode, navigate back
                navigate(-1);
            }
        }
    }, [controlledOnClose, close, navigate, isModalMode, normalizedDocType, queryClient, location]);

    const [visible, { open: openLoading, close: closeLoading }] = useDisclosure(false);
    const handleBusyChange = useCallback((busy: boolean) => {
        if (busy) {
            openLoading();
        } else {
            closeLoading();
        }
    }, [openLoading, closeLoading]);

    const normalizedAction = (action ?? '').toLowerCase();

    const actionError = useMemo(() => {
        if (!normalizedDocType) {
            return 'Document type is required to open this form.';
        }
        if (!normalizedAction) {
            return 'Form action is missing. Please return to the previous page and try again.';
        }
        if (!['add', 'edit', 'view'].includes(normalizedAction)) {
            return `Unsupported form action "${action}". Only add, edit, or view are allowed.`;
        }
        if ((normalizedAction === 'edit' || normalizedAction === 'view') && !docNo) {
            return `A document number is required to ${normalizedAction} this record.`;
        }
        return null;
    }, [normalizedDocType, normalizedAction, action, docNo]);

    // Auto-open modal if in modal mode and not controlled
    React.useEffect(() => {
        if (isModalMode && controlledOpened === undefined) {
            open();
        }
    }, [isModalMode, controlledOpened, open]);

    const actionControls = useMemo(() => {
        if (!normalizedDocType || !documentConfig) {
            return [];
        }

        const basePath = `/form/${normalizedDocType}`;
        const configPermissions = documentConfig.permissions ?? {};

        return [
            {
                key: 'add',
                label: 'Add',
                path: `${basePath}/add`,
                enabled: Boolean(configPermissions.add),
            },
            {
                key: 'edit',
                label: 'Edit',
                path: docNo ? `${basePath}/edit/${docNo}` : null,
                enabled: Boolean(docNo && configPermissions.edit),
            },
            {
                key: 'view',
                label: 'View',
                path: docNo ? `${basePath}/view/${docNo}` : null,
                enabled: Boolean(docNo),
            },
        ].map((item) => ({
            ...item,
            active: item.key === normalizedAction,
        }));
    }, [normalizedDocType, documentConfig, docNo, normalizedAction]);

    const actionHeading = normalizedAction
        ? normalizedAction.charAt(0).toUpperCase() + normalizedAction.slice(1)
        : '';

    const modalTitle = propModalTitle ?? `${documentConfig?.formName ?? ''} | ${actionHeading}`;

    const changeDisplayMode = useCallback(
        (mode: FormDisplayMode) => {
            if (!normalizedDocType || !normalizedAction) return;

            const basePath = `/form/${normalizedDocType}/${normalizedAction}`;
            const path = docNo ? `${basePath}/${docNo}` : basePath;

            if (mode === 'navigation') {
                const currentSearch = new URLSearchParams(location.search);
                const preservedParams = new URLSearchParams();
                currentSearch.forEach((value, key) => {
                    if (!key.startsWith('form')) {
                        preservedParams.set(key, value);
                    }
                });
                
                const cleanPath = preservedParams.toString() 
                    ? `${path}?${preservedParams.toString()}`
                    : path;
                
                navigate(cleanPath, { 
                    replace: true,
                    state: undefined
                });
                return;
            }

            if (isModalFromQuery) {
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.set('formDocType', normalizedDocType);
                newSearchParams.set('formAction', normalizedAction);
                if (docNo) {
                    newSearchParams.set('formDocNo', docNo);
                } else {
                    newSearchParams.delete('formDocNo');
                }
                newSearchParams.set('formModal', 'true');
                newSearchParams.set('formDisplayMode', mode);
                
                navigate(`${location.pathname}?${newSearchParams.toString()}`, {
                    state: {
                        programmaticOpen: true,
                        displayMode: mode,
                    },
                    replace: true,
                });
            } else {
                navigate(path, {
                    state: {
                        programmaticOpen: true,
                        displayMode: mode,
                    },
                    replace: true,
                });
            }
        },
        [normalizedDocType, normalizedAction, docNo, navigate, location.search, location.pathname, isModalFromQuery]
    );

    const cycleDisplayMode = useCallback(() => {
        const order: FormDisplayMode[] = ['navigation', 'modal', 'fullscreen', 'sidebar'];
        const currentIndex = order.indexOf(displayMode);
        const nextMode = currentIndex === -1 ? 'modal' : order[(currentIndex + 1) % order.length];
        changeDisplayMode(nextMode);
    }, [changeDisplayMode, displayMode]);

    const handleViewLogs = useCallback(() => {
        console.log('Open logs view for', {
            docType: normalizedDocType,
            action: normalizedAction,
            docNo,
        });
    }, [normalizedDocType, normalizedAction, docNo]);

    const renderContent = useMemo(() => {
        if (actionError) {
            // When FormLayout is controlled externally (e.g. inside GlobalFormModal), the action
            // becomes empty for one render cycle while the modal closes after a save-navigation.
            // Return null to avoid a flash of the error; the parent will unmount this shortly.
            if (controlledOnClose) return null;
            return (
                <Alert color="red" variant="light" title="Cannot load form">
                    {actionError}
                </Alert>
            );
        }

        if (!documentConfig) {
            return (
                <Alert color="orange" variant="light" title="Resource not found">
                    We could not find the configuration for this document. Please verify your access or try again later.
                </Alert>
            );
        }

        const searchParams = location.search ? location.search.substring(1) : undefined;

        // Try to read audit fields from form query cache (populated by useGetForm / useCustomForm)
        const formQueryKey = [docType, docNo ?? '', searchParams];
        const formQueryData = queryClient.getQueryData(formQueryKey) as
            | { form?: { createdBy?: string; createdTs?: string; updatedBy?: string; updatedTs?: string } }
            | undefined;
        const auditSource = formQueryData?.form;

        const createdBy = auditSource?.createdBy ?? '';
        const createdTs = auditSource?.createdTs ?? '';
        const updatedBy = auditSource?.updatedBy ?? '';
        const updatedTs = auditSource?.updatedTs ?? '';

        const hasAuditData = Boolean(createdBy || createdTs || updatedBy || updatedTs);

        const iconMode: FormDisplayMode =
            displayMode === 'navigation' ||
                displayMode === 'fullscreen' ||
                displayMode === 'sidebar' ||
                displayMode === 'modal'
                ? displayMode
                : 'navigation';

        const modeOptions: { key: FormDisplayMode; label: string; icon: React.ReactNode }[] = [
            { key: 'navigation', label: 'Default view', icon: <IconLayout2 size={14} /> },
            { key: 'modal', label: 'Modal', icon: <IconSquare size={14} /> },
            { key: 'fullscreen', label: 'Full screen', icon: <IconMaximize size={14} /> },
            { key: 'sidebar', label: 'Sidebar', icon: <IconLayoutSidebarRight size={14} /> },
        ];

        const formContent = (
            <Paper
                withBorder
                p="8"
                style={isModalMode ? { minHeight: 'auto' } : { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
                className={
                    displayMode === 'sidebar'
                        ? 'h-full flex flex-col'
                        : undefined
                }
            >
                {!isModalMode && (
                    <div
                        className="sticky top-0 z-40 mb-2 flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 shadow-xs"
                        style={{
                            borderColor: 'var(--app-border)',
                            backgroundColor: 'var(--app-surface)',
                            boxShadow: 'var(--app-shadow-xs)',
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="flex h-8 w-8 items-center justify-center rounded-md text-white shadow-sm"
                                style={{ backgroundColor: 'var(--mantine-color-brand-6)' }}
                            >
                                <IconFile className="h-4 w-4" stroke={1.8} />
                            </div>
                            <div className="flex flex-col">
                                <p className="text-sm font-semibold leading-tight">
                                    {documentConfig?.formName}
                                </p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                                    {docNo && (
                                        <span className="rounded-full px-2 py-0.5 font-mono text-[11px]"
                                            style={{
                                                backgroundColor: 'var(--app-surface-muted)',
                                                color: 'var(--app-text-muted)',
                                            }}
                                        >
                                            #{docNo}
                                        </span>
                                    )}
                                    <Text size="xs" c="dimmed">
                                        {docNo
                                            ? `${actionHeading} record`
                                            : `${actionHeading || 'Add'} new record`}
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <Group gap="xs" align="center" wrap="nowrap">
                            {/* Display mode switcher: click to cycle, hover to choose */}
                            <Menu trigger="hover" openDelay={100} closeDelay={150} withinPortal>
                                <Menu.Target>
                                    <Tooltip label="Change layout" withArrow>
                                        <ActionIcon
                                            variant="light"
                                            color="blue"
                                            size="sm"
                                            radius="xl"
                                            onClick={cycleDisplayMode}
                                        >
                                            {iconMode === 'sidebar' && <IconLayoutSidebarRight size={16} />}
                                            {iconMode === 'fullscreen' && <IconMaximize size={16} />}
                                            {iconMode === 'navigation' && <IconLayout2 size={16} />}
                                            {iconMode === 'modal' && <IconSquare size={16} />}
                                        </ActionIcon>
                                    </Tooltip>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {modeOptions.map((mode) => (
                                        <Menu.Item
                                            key={mode.key}
                                            leftSection={mode.icon}
                                            rightSection={mode.key === iconMode ? <IconCheck size={14} /> : null}
                                            onClick={() => changeDisplayMode(mode.key)}
                                            fw={mode.key === iconMode ? 600 : 400}
                                        >
                                            {mode.label}
                                        </Menu.Item>
                                    ))}
                                </Menu.Dropdown>
                            </Menu>

                            <Group gap={4} wrap="nowrap">
                                {actionControls.map((item) => (
                                    <Button
                                        key={item.key}
                                        size="xs"
                                        radius="xl"
                                        variant={item.active ? 'filled' : 'subtle'}
                                        color={item.active ? 'blue' : 'gray'}
                                        disabled={!item.enabled || !item.path || item.active}
                                        onClick={() => item.path && navigate(item.path)}
                                    >
                                        {item.label}
                                    </Button>
                                ))}
                            </Group>

                            {normalizedDocType && documentConfig.permissions?.add && (
                                <DocumentMenu
                                    docType={normalizedDocType}
                                    docNo={docNo ?? ''}
                                    onBusyChange={handleBusyChange}
                                />
                            )}
                        </Group>
                    </div>
                )}
                {isModalMode && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div
                                className="flex h-7 w-7 items-center justify-center rounded-md text-white"
                                style={{ backgroundColor: 'var(--mantine-color-brand-6)' }}
                            >
                                <IconFile className="h-3 w-3" stroke={1.8} />
                            </div>
                            <div className="flex flex-col">
                                <p className="text-sm font-semibold leading-tight">
                                    {documentConfig?.formName}
                                </p>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                                    {docNo && (
                                        <span className="rounded-full px-2 py-0.5 font-mono text-[11px]"
                                            style={{
                                                backgroundColor: 'var(--app-surface-muted)',
                                                color: 'var(--app-text-muted)',
                                            }}
                                        >
                                            #{docNo}
                                        </span>
                                    )}
                                    <Text size="xs" c="dimmed">
                                        {docNo
                                            ? `${actionHeading} record`
                                            : `${actionHeading || 'Add'} new record`}
                                    </Text>
                                </div>
                            </div>
                        </div>
                        <Group gap="xs" align="center">
                            {/* Display mode switcher: click to cycle, hover to choose */}
                            <Menu trigger="hover" openDelay={100} closeDelay={150} withinPortal>
                                <Menu.Target>
                                    <Tooltip label="Change layout" withArrow>
                                        <ActionIcon
                                            variant="light"
                                            color="blue"
                                            size="sm"
                                            radius="xl"
                                            onClick={cycleDisplayMode}
                                        >
                                            {iconMode === 'sidebar' && <IconLayoutSidebarRight size={16} />}
                                            {iconMode === 'fullscreen' && <IconMaximize size={16} />}
                                            {iconMode === 'navigation' && <IconLayout2 size={16} />}
                                            {iconMode === 'modal' && <IconSquare size={16} />}
                                        </ActionIcon>
                                    </Tooltip>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {modeOptions.map((mode) => (
                                        <Menu.Item
                                            key={mode.key}
                                            leftSection={mode.icon}
                                            rightSection={mode.key === iconMode ? <IconCheck size={14} /> : null}
                                            onClick={() => changeDisplayMode(mode.key)}
                                            fw={mode.key === iconMode ? 600 : 400}
                                        >
                                            {mode.label}
                                        </Menu.Item>
                                    ))}
                                </Menu.Dropdown>
                            </Menu>

                            <Group gap={4}>
                                {actionControls.map((item) => (
                                    <Button
                                        key={item.key}
                                        size="xs"
                                        radius="xl"
                                        variant={item.active ? 'filled' : 'subtle'}
                                        color={item.active ? 'blue' : 'gray'}
                                        disabled={!item.enabled || !item.path || item.active}
                                        onClick={() => item.path && navigate(item.path)}
                                    >
                                        {item.label}
                                    </Button>
                                ))}
                            </Group>

                            {normalizedDocType && documentConfig.permissions?.add && (
                                <DocumentMenu
                                    docType={normalizedDocType}
                                    docNo={docNo ?? ''}
                                    onBusyChange={handleBusyChange}
                                />
                            )}
                        </Group>
                    </div>
                )}
                {/* Minimal audit summary - only render when data exists */}
                {hasAuditData && (
                    <div
                        className="mb-3 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-1.5 text-xs"
                        style={{
                            backgroundColor: 'var(--app-surface-muted)',
                            color: 'var(--app-text-muted)',
                        }}
                    >
                        {createdBy && (
                            <span className="flex items-center gap-1">
                                <span className="font-semibold text-(--app-text)">By</span>
                                <span className="text-(--app-text)">{createdBy}</span>
                            </span>
                        )}
                        {createdTs && (
                            <span className="flex items-center gap-1">
                                <span
                                    className="h-1 w-1 rounded-full"
                                    style={{ backgroundColor: 'var(--app-border)' }}
                                />
                                <span className="font-semibold" style={{ color: 'var(--app-text)' }}>Created</span>
                                <RelativeTime value={createdTs} />
                            </span>
                        )}
                        {updatedBy && (
                            <span className="flex items-center gap-1">
                                <span
                                    className="h-1 w-1 rounded-full"
                                    style={{ backgroundColor: 'var(--app-border)' }}
                                />
                                <span className="font-semibold" style={{ color: 'var(--app-text)' }}>Updated by</span>
                                <span style={{ color: 'var(--app-text)' }}>{updatedBy}</span>
                            </span>
                        )}
                        {updatedTs && (
                            <span className="flex items-center gap-1">
                                <span
                                    className="h-1 w-1 rounded-full"
                                    style={{ backgroundColor: 'var(--app-border)' }}
                                />
                                <span className="font-semibold" style={{ color: 'var(--app-text)' }}>Updated</span>
                                <RelativeTime value={updatedTs} />
                            </span>
                        )}
                    </div>
                )}

                <Box pos="relative" style={isModalMode ? undefined : { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <LoadingOverlay visible={visible} />
                    {children}
                </Box>
            </Paper>
        );

        if (isModalMode) {
            // Sidebar mode: use Mantine Drawer
            if (displayMode === 'sidebar') {
                return (
                    <Drawer
                        opened={modalOpened}
                        onClose={handleModalClose}
                        title={modalTitle}
                        position="right"
                        size="xl"
                        padding="md"
                        overlayProps={{ opacity: 0.2, blur: 2 }}
                    >
                        {formContent}
                    </Drawer>
                );
            }

            // If hideModalWrapper is true, just return the content (modal wrapper is handled by parent Modal.Stack)
            if (hideModalWrapper) {
                return formContent;
            }

            // Modal / fullscreen modes: use Mantine Modal
            let modalProps: { fullScreen?: boolean; size: string; styles?: Record<string, any> };
            if (displayMode === 'fullscreen') {
                modalProps = {
                    fullScreen: true,
                    size: '100%',
                };
            } else if (displayMode === 'modal') {
                modalProps = {
                    size: '95%',
                    styles: {
                        content: {
                            maxWidth: '95vw !important',
                            width: '95vw !important',
                        },
                    },
                };
            } else {
                modalProps = {
                    size: modalSize,
                };
            }

            return (
                <Modal
                    opened={modalOpened}
                    onClose={handleModalClose}
                    title={modalTitle}
                    centered
                    closeButtonProps={{ 'aria-label': 'Close modal' }}
                    {...modalProps}
                >
                    {formContent}
                </Modal>
            );
        }

        return (
            <Paper style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Head title={`${documentConfig?.formName} | ${actionHeading}`} />
                {formContent}
            </Paper>
        );
    }, [
        actionError,
        documentConfig,
        actionHeading,
        actionControls,
        normalizedDocType,
        docNo,
        handleBusyChange,
        navigate,
        visible,
        children,
        isModalMode,
        modalOpened,
        handleModalClose,
        modalTitle,
        modalSize,
        displayMode,
        changeDisplayMode,
        cycleDisplayMode,
        handleViewLogs,
        docType,
        docNo,
        location.search,
        queryClient,
    ]);

    return (
        <FormNavigationContext.Provider value={isModalMode ? { closeModal: handleModalClose } : {}}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>{renderContent}</div>
        </FormNavigationContext.Provider>
    );
};
